/**
 * Composants métier réutilisables (vin) : pastille de couleur, cœurs, kicker couleur,
 * valeur masquée (mode public), tags de spécificités, tag de statut, badge de source.
 */
import { Heart, Eye } from 'lucide-react';
import { WINE_COLOR_DOTS, WINE_TYPE_LABELS, SPECIFICITY_LABELS, STATUS_LABELS, SOURCE_LABELS } from '../../constants';
import { Tag } from '../ui/kit';

/** Pastille ronde de couleur du vin (fin contour sombre). */
export function WineColorDot({ color, size = 11, className = '' }) {
  const dot = WINE_COLOR_DOTS[color] || WINE_COLOR_DOTS.rouge;
  return (
    <span
      className={`inline-block rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size, background: dot.color, border: '1px solid rgba(32,31,29,0.22)' }}
      title={dot.label}
      data-testid={`wine-dot-${color}`}
    />
  );
}

/** Cœurs coup de cœur (0-3). Cliquables si onChange fourni. */
export function Hearts({ count = 0, onChange, size = 15 }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="hearts">
      {[1, 2, 3].map((i) => {
        const filled = i <= count;
        const Icon = (
          <Heart
            style={{ width: size, height: size }}
            className={filled ? 'text-gold fill-gold' : 'text-n500'}
            strokeWidth={1.6}
          />
        );
        return onChange ? (
          <button key={i} data-testid={`heart-${i}`} onClick={() => onChange(i === count ? i - 1 : i)} className="hover:scale-110 transition-transform">
            {Icon}
          </button>
        ) : (
          <span key={i}>{Icon}</span>
        );
      })}
    </span>
  );
}

/** Kicker « pastille · COULEUR · TYPE · APPELLATION · RÉGION ». */
export function ColorKicker({ color, type, appellation, region }) {
  const parts = [WINE_COLOR_DOTS[color]?.label, WINE_TYPE_LABELS[type], appellation, region].filter(Boolean);
  return (
    <div className="flex items-center gap-2 kicker">
      <WineColorDot color={color} size={9} />
      <span>{parts.join(' · ')}</span>
    </div>
  );
}

/** Valeur financière masquée en mode public (affiche •••). */
export function MaskedValue({ masked, children, className = '' }) {
  if (masked) return <span className={`text-n500 ${className}`} data-testid="masked-value">•••</span>;
  return <span className={className}>{children}</span>;
}

/** Tags de spécificités viticoles (les premières en étiquettes, le reste en texte discret). */
export function SpecificityTags({ keys = [], max = 3 }) {
  const shown = keys.slice(0, max);
  const rest = keys.slice(max).map((k) => SPECIFICITY_LABELS[k] || k);
  return (
    <span className="inline-flex flex-wrap gap-1.5 items-center">
      {shown.map((k) => (
        <Tag key={k} tone="accent">{SPECIFICITY_LABELS[k] || k}</Tag>
      ))}
      {rest.length > 0 && <span className="text-n500 text-xs">{rest.join(' · ')}</span>}
    </span>
  );
}

/** Tag de statut d'une cuvée (rupture, stock faible, à boire, récent). */
export function StatusTag({ status }) {
  if (!status) return null;
  const tone = status === 'a_boire' ? 'fill' : status === 'rupture' ? 'accent' : status === 'stock_faible' ? 'accent' : 'neutral';
  return <Tag tone={tone} data-testid={`status-${status}`}>{STATUS_LABELS[status] || status}</Tag>;
}

/** Badge indiquant la source d'un champ pré-rempli (IA). */
export function SourceBadge({ source, toVerify }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide ${toVerify ? 'text-gold700' : 'text-n500'}`}>
      <Eye className="w-3 h-3" strokeWidth={1.6} /> {SOURCE_LABELS[source] || source}
    </span>
  );
}
